<template>
 
    <div v-if="hasProjectIdInDefaults" class="col-12 form__control-group field-group">
      <label class="label-spacing">Project</label>
      <UidName :id="props.defaultValues.project_id" :name="props.defaultValues.project_name || ''" prefix="p" />
    </div>
    <div v-else-if="enableProjectSelection" class="col-12 form__control-group field-group">
      <label class="label-spacing"  :for="`select-project-input-${parent}-${schemaField.name}`">
        Project
        <SVGIcon
          v-if="schemaField.description"
          v-tooltip="schemaField.description"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-project-input-${parent}-${schemaField.name}`"
        :value="selectedProjectId"
        class="select"
        @input="selectedProjectId = parseInt($event.target.value)"
      >
        <option value="" disabled selected>Select Project</option>
        <option
          v-for="(project, i) in projects"
          :key="`dropdown-project-${parent}-${schemaField.name}-${i}-${project.id}`"
          :value="project.id"
        >
          {{ project.name }}
        </option>
      </select>
    </div>

    <div class="col-12 form__control-group field-group">
      <label class="label-spacing"  :for="`select-dataset-input-${parent}-${schemaField.name}`">
       Dataset
        <SVGIcon
          v-if="schemaField.description"
          v-tooltip="schemaField.description"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-dataset-input-${parent}-${schemaField.name}`"
        :value="internalDatasetId"
        class="select"
        @input="onChangeDataset(parseInt($event.target.value))"
      >
        <option value="" disabled selected>Select Dataset</option>
        <option
          v-for="(dataset, i) in datasets"
          :key="`dropdown-${parent}-${schemaField.name}-${i}-${dataset.name}`"
          :value="dataset.id"
        >
          {{ dataset.name }}
        </option>
      </select>
    </div>

    <div v-if="enableTagsSelection" class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-tag-input-${parent}-${schemaField.name}`">
        Tag
        <SVGIcon
          v-if="schemaField.description"
          v-tooltip="'Select a tag version'"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-tag-input-${parent}-${schemaField.name}`"
        v-model="selectedTag"
        class="select"
      >
        <option value="" disabled selected>Select Tag</option>
        <option
          v-for="(tag, i) in tags"
          :key="`dropdown-tag-${parent}-${schemaField.name}-${i}-${tag.name}`"
          :value="tag.name"
        >
          {{ tag.name }}
        </option>
      </select>
    </div>

    <div v-if="enableAnnotationSetSelection && datasetId" class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-annotation-set-input-${parent}-${schemaField.name}`">
        Annotation Set
        <SVGIcon
          v-if="schemaField.description"
          v-tooltip="'Select an annotation set'"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-annotation-set-input-${parent}-${schemaField.name}`"
        v-model="selectedAnnotationSet"
        class="select"
      >
        <option value="" disabled selected>Select Annotation Set</option>
        <option
          v-for="(annotationSet, i) in annotationSets"
          :key="`dropdown-annotation-set-${parent}-${schemaField.name}-${i}-${annotationSet.name}`"
          :value="annotationSet.id"
        >
          {{ annotationSet.name }}
        </option>
      </select>
    </div>

    <div v-if="enableGroupSelection && datasetId" class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-train-group-input-${parent}-${schemaField.name}`">
        Train Group
        <SVGIcon
          v-if="schemaField.description"
          v-tooltip="'Select a training group'"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-train-group-input-${parent}-${schemaField.name}`"
        v-model="selectedTrainGroup"
        class="select"
      >
        <option value="" disabled selected>Select Train Group</option>
        <option
          v-for="(group, i) in groups"
          :key="`dropdown-train-group-${parent}-${schemaField.name}-${i}-${group.name}`"
          :value="group"
        >
          {{ group.name }}
        </option>
      </select>
    </div>

    <div v-if="enableGroupSelection && datasetId" class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-val-group-input-${parent}-${schemaField.name}`">
        Val Group
        <SVGIcon
          v-if="schemaField.description"
          v-tooltip="'Select a validation group'"
          :width="'20px'"
          :height="'20px'"
          :iconName="'info'"
          class="help-logo"
        />
      </label>
      <select
        :id="`select-val-group-input-${parent}-${schemaField.name}`"
        v-model="selectedValGroup"
        class="select"
      >
        <option value="" disabled selected>Select Val Group</option>
        <option
          v-for="(group, i) in groups"
          :key="`dropdown-val-group-${parent}-${schemaField.name}-${i}-${group.name}`"
          :value="group"
        >
          {{ group.name }}
        </option>
      </select>

  </div>
</template>

<script setup>
import {
  ref, computed, toRefs, onMounted, watch,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useStore } from 'vuex';
import SVGIcon from '@/components/SVGIcon.vue';
import UidName from '@/components/Schema/UidName.vue';

const props = defineProps({
  parent: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [Number, String, Object],
    default: null,
  },
  enableTagsSelection: {
   type: Boolean,
    default: true,
  },
  enableAnnotationSetSelection: {
   type: Boolean,
    default: false,
  },
  enableGroupSelection: {
   type: Boolean,
    default: false,
  },
   enableProjectSelection: {
   type: Boolean,
    default: true,
  },
  schemaField: {
    type: Object,
    default: () => {},
  },
  datasetId: {
    type: [Number, String],
    default: null,
  },
  tagName: {
    type: String,
    default: '',
  },  
  annotationSetId: {
    type: [Number, String],
    default: null,
  },
  trainGroupName: {
    type: String,
    default: '',
  },
  valGroupName: {
    type: String,
    default: '',
  },
  defaultValues: {
    type: Object,
    default: () => ({}),
  },
});

const {
  parent,
  schemaField,
  modelValue,
} = toRefs(props);

const hasProjectIdInDefaults = computed(() => props.defaultValues?.project_id !== undefined);

const emit = defineEmits(['update:modelValue', 'update:datasetId', 'update:tagName', 'update:annotationSetId', 'update:trainGroupName', 'update:valGroupName']);

// Use modelValue for v-model binding, fallback to datasetId prop
const internalDatasetId = computed({
  get: () => {
    if (modelValue?.value) {
      // If modelValue is an object with dataset_id property, extract it
      if (typeof modelValue.value === 'object' && modelValue.value.dataset_id) {
        return modelValue.value.dataset_id;
      }
      return modelValue.value;
    }
    return props.datasetId;
  },
  set: (val) => {
    const datasetObj = {
      dataset_id: val,
      project_id: selectedProjectId.value || currentProject.value?.id,
      tag_name: selectedTag.value
    };
    emit('update:modelValue', datasetObj);
    emit('update:datasetId', val);
  }
});

async function getDatasets() {
  const projectId = selectedProjectId.value || currentProject.value?.id;
  if (projectId) {
    const dataConnect = new DatastoreConnect();
    const datasets = await dataConnect.getDatasetList({
      project_id: projectId,
      get_annotation_sets: true,
      get_groups: true,
    })
      .then((resp) => {
        if (resp.error) {
          throw resp.error;
        }
        return resp.result;
      })
      .catch((error) => {
        console.error(error);
      });

    console.log(datasets);
    return datasets;
  }
  return [];
}

const store = useStore();
const currentProject = computed(() => store.state.projects.currentProject);
const projects = ref([]);
const selectedProjectId = ref(null);
const datasets = ref(null);
const tags = ref([]);
const selectedTag = ref('');
const annotationSets = ref([]);
const selectedAnnotationSet = ref('');
const groups = ref([]);
const selectedTrainGroup = ref('');
const selectedValGroup = ref('');

async function getProjects() {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('project.list', {});
    
    if (response.error) {
      throw response.error;
    }
    return response.result || [];
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

async function getTags(datasetId) {
  if (!datasetId) {
    tags.value = [];
    selectedTag.value = '';
    return;
  }
  
  const dataConnect = new DatastoreConnect();
  const tagList = await dataConnect.api("version.tag.list", {
    dataset_id: parseInt(datasetId),
  })
    .then((resp) => {
      if (resp.error) {
        throw resp.error;
      }
      return resp.result;
    })
    .catch((error) => {
      console.error(error);
      return [];
    });
  
  tags.value = tagList;
  
  // Set default to the latest (last) tag
  if (props.tagName) {
    selectedTag.value = props.tagName;
  } else{
    if (tags.value && tags.value.length > 0) {
      selectedTag.value = tags.value[0].name;
    }
  }
}

async function getAnnotationSets(datasetId) {
  if (!datasetId) {
    annotationSets.value = [];
    selectedAnnotationSet.value = '';
    return;
  }
  
  const dataset = datasets.value?.find(d => d.id === parseInt(datasetId));
  if (dataset && dataset.annotation_sets) {
    annotationSets.value = dataset.annotation_sets;
   
   if (props.annotationSetId) {
      selectedAnnotationSet.value = props.annotationSetId;
    } else {
      // Set default to the first annotation set
      if (annotationSets.value.length > 0) {
        selectedAnnotationSet.value = annotationSets.value[0].id;
      }
    }
  } else {
    annotationSets.value = [];
    selectedAnnotationSet.value = '';
  }
}

async function getGroups(datasetId) {
  if (!datasetId) {
    groups.value = [];
    selectedTrainGroup.value = '';
    selectedValGroup.value = '';
    return;
  }
  
  const dataset = datasets.value?.find(d => d.id === parseInt(datasetId));
  if (dataset && dataset.groups) {
    groups.value = dataset.groups;
    
    // Set default train group to any group with 'train' in the name (case-insensitive)
    const trainGroup = groups.value.find(g => g.name.toLowerCase().includes('train'));
    if (trainGroup) {
      selectedTrainGroup.value = trainGroup;
    }
    
    // Set default val group to any group with 'val' in the name (case-insensitive)
    const valGroup = groups.value.find(g => g.name.toLowerCase().includes('val'));
    if (valGroup) {
      selectedValGroup.value = valGroup;
    }
  } else {
    groups.value = [];
    selectedTrainGroup.value = '';
    selectedValGroup.value = '';
  }
}


function onChangeDataset(dsID) {
   const datasetObj = {
     dataset_id: dsID,
     project_id: selectedProjectId.value || currentProject.value?.id,
     tag_name: selectedTag.value
   };
   emit('update:modelValue', datasetObj);
   emit('update:datasetId', dsID);

   if (props.enableTagsSelection && dsID) {
    getTags(dsID);
  } else {
    tags.value = [];
    selectedTag.value = '';
  }
  
  if (props.enableAnnotationSetSelection && dsID) {
    getAnnotationSets(dsID);
  } else {
    annotationSets.value = [];
    selectedAnnotationSet.value = '';
  }
  
  if (props.enableGroupSelection && dsID) {
    getGroups(dsID);
  } else {
    groups.value = [];
    selectedTrainGroup.value = '';
    selectedValGroup.value = '';
  }
  
}

watch(selectedProjectId, async (newValue) => {
  if (newValue) {
    // Reload datasets when project changes
    datasets.value = await getDatasets();
  }
});

watch(internalDatasetId, (newValue) => {
  if (newValue) {
    onChangeDataset(newValue);
  }
});

watch(selectedTag, (newValue) => {
  emit('update:tagName', newValue);
  
  // Also update the modelValue with the new tag_name
  const datasetObj = {
    dataset_id: internalDatasetId.value,
    project_id: selectedProjectId.value || currentProject.value?.id,
    tag_name: newValue
  };
  emit('update:modelValue', datasetObj);
});

watch(selectedAnnotationSet, (newValue) => {
  emit('update:annotationSetId', newValue);
});

watch(selectedTrainGroup, (newValue) => {
  emit('update:trainGroupName', newValue?.name || '');
});

watch(selectedValGroup, (newValue) => {
  emit('update:valGroupName', newValue?.name || '');
});

onMounted(async () => {
  // If defaultValues provide project_id, emit it as modelValue immediately
  if (props.defaultValues?.project_id !== undefined) {
    emit('update:modelValue', {
      dataset_id: null,
      project_id: props.defaultValues.project_id,
      tag_name: null,
    });
  }

  // Fetch available projects
  projects.value = await getProjects();
  
  // If modelValue is passed as an object, extract values from it
  let initialProjectId = null;
  let initialDatasetId = null;
  let initialTagName = '';
  
  if (modelValue?.value && typeof modelValue.value === 'object') {
    initialProjectId = modelValue.value.project_id;
    initialDatasetId = modelValue.value.dataset_id;
    initialTagName = modelValue.value.tag_name || '';
  }
  
  // Set default project to current project or passed project_id
  if (initialProjectId) {
    selectedProjectId.value = initialProjectId;
  } else if (currentProject.value) {
    selectedProjectId.value = currentProject.value.id;
  } else if (projects.value && projects.value.length > 0) {
    selectedProjectId.value = projects.value[0].id;
  }
  
  datasets.value = await getDatasets();
  let dsID = initialDatasetId || props.datasetId || 0;
  
  if (dsID) {
    let ds = datasets.value.find(d => d.id === parseInt(dsID));
    if (ds) {
      dsID = ds.id;
    }
  } else {
    // Set default dataset to the first one in the list
    if (datasets.value && datasets.value.length > 0) {
      dsID = datasets.value[0].id;
    }
  }
  
  // Set tag name if provided
  if (initialTagName) {
    selectedTag.value = initialTagName;
  }
  
  onChangeDataset(dsID);
});
</script>

<style scoped>
.label-spacing {
  display: block;
  margin-top: 6px;
  margin-bottom: 4px;
}
</style>
